import type { Case, WorkflowTemplate, FastSnapshot, AuctionInfo } from "@auction-eth/shared-types";
import type {
  CreateCaseBody,
  ListCasesResponse,
  GetCaseResponse,
  UpdateFlagBody,
  UpdateFlagResponse,
} from "@auction-eth/shared-types";
import { getAccessToken, setTokens, clearTokens, getRefreshToken } from "./auth.js";

const BASE = "/api";

async function request<T>(
  path: string,
  options?: RequestInit
): Promise<T> {
  const token = getAccessToken();
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const res = await fetch(`${BASE}${path}`, {
    headers,
    ...options,
  });

  // Attempt token refresh on 401
  if (res.status === 401) {
    const refreshToken = getRefreshToken();
    if (refreshToken) {
      const refreshRes = await fetch(`${BASE}/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });
      if (refreshRes.ok) {
        const { data } = await refreshRes.json() as { data: { access_token: string; refresh_token: string } };
        setTokens(data.access_token, data.refresh_token);
        // Retry original request
        const retryRes = await fetch(`${BASE}${path}`, {
          headers: { ...headers, Authorization: `Bearer ${data.access_token}` },
          ...options,
        });
        if (!retryRes.ok) {
          const body = await retryRes.text();
          throw new Error(`API error ${retryRes.status}: ${body}`);
        }
        return retryRes.json() as Promise<T>;
      }
    }
    clearTokens();
    window.location.reload();
  }

  if (!res.ok) {
    const body = await res.text();
    throw new Error(`API error ${res.status}: ${body}`);
  }
  return res.json() as Promise<T>;
}

export const api = {
  cases: {
    list(params?: { is_closed?: boolean; lender_id?: string }): Promise<ListCasesResponse> {
      const q = new URLSearchParams();
      if (params?.is_closed !== undefined) q.set("is_closed", String(params.is_closed));
      if (params?.lender_id) q.set("lender_id", params.lender_id);
      const qs = q.toString();
      return request<ListCasesResponse>(`/cases${qs ? `?${qs}` : ""}`);
    },

    get(caseId: string): Promise<GetCaseResponse> {
      return request<GetCaseResponse>(`/cases/${caseId}`);
    },

    create(body: CreateCaseBody): Promise<GetCaseResponse> {
      return request<GetCaseResponse>("/cases", {
        method: "POST",
        body: JSON.stringify(body),
      });
    },

    updateFlag(caseId: string, body: UpdateFlagBody): Promise<UpdateFlagResponse> {
      return request<UpdateFlagResponse>(`/cases/${caseId}/flags`, {
        method: "PATCH",
        body: JSON.stringify(body),
      });
    },

    submitModule(
      caseId: string,
      moduleId: string,
      output: Record<string, unknown>,
      actorId: string
    ): Promise<{
      data: {
        case_id: string;
        module_id: string;
        step_id: string;
        locked: boolean;
        gate_handled_flag: boolean;
        flags_updated?: { start_flag: string; complete_flag: string };
      };
    }> {
      const mid = encodeURIComponent(moduleId);
      return request(`/cases/${caseId}/modules/${mid}/submit`, {
        method: "POST",
        body: JSON.stringify({ actor_id: actorId, output }),
      });
    },

    updatePreAuctionSetup(
      caseId: string,
      auctionInfo: Partial<AuctionInfo>,
      actorId: string
    ): Promise<{ data: { case_id: string; auction_info: Partial<AuctionInfo> } }> {
      return request(`/cases/${caseId}/pre-auction-setup`, {
        method: "PATCH",
        body: JSON.stringify({ auction_info: auctionInfo, actor_id: actorId }),
      });
    },

    appendMemo(
      caseId: string,
      text: string,
      actorId: string
    ): Promise<{ data: { case_id: string; memo_count: number } }> {
      return request(`/cases/${caseId}/memo`, {
        method: "PATCH",
        body: JSON.stringify({ text, actor_id: actorId }),
      });
    },

    /** Optional `lender_id` filter (platform admin / NOC). Institution app sends JWT institution id. */
    metrics(lenderId?: string): Promise<{
      data: {
        summary: {
          total_active: number;
          total_closed: number;
          total_all: number;
          recovered_last_30d: number;
          stalled: number;
          recovery_rate: number;
        };
        by_stage: Record<string, number>;
        step_distribution: Array<{ step: string; count: number }>;
        generated_at: string;
      };
    }> {
      const qs = lenderId ? `?lender_id=${encodeURIComponent(lenderId)}` : "";
      return request(`/cases/metrics${qs}`);
    },

    history(caseId: string): Promise<{
      data: {
        case_id: string;
        history: Array<{
          step_id: string;
          status: string;
          started_at: string | null;
          completed_at: string | null;
          actor_id: string | null;
        }>;
        commitment_boundaries_crossed: string[];
      };
    }> {
      return request(`/cases/${caseId}/history`);
    },

    auditTrail(caseId: string): Promise<{
      data: Array<{
        event_type: string;
        case_id?: string;
        actor_id?: string;
        application?: string;
        details?: Record<string, unknown>;
        occurred_at?: string;
        _id?: string;
      }>;
      count: number;
    }> {
      return request(`/cases/${caseId}/audit`);
    },
  },

  templates: {
    list(): Promise<{ data: WorkflowTemplate[] }> {
      return request<{ data: WorkflowTemplate[] }>("/registry/templates?status=active");
    },
    get(templateId: string): Promise<{ data: WorkflowTemplate & { sequence?: Record<string, { module?: string; application?: string }> } }> {
      return request(`/registry/templates/${encodeURIComponent(templateId)}`);
    },
  },

  fast: {
    lookup(loanRef: string): Promise<{
      data: {
        loan_detail: Record<string, unknown>;
        borrower_info: Record<string, unknown>;
        asset_info: Record<string, unknown>;
        fetched_at: string;
        source: "fast_api" | "simulation" | "stub";
      };
    }> {
      return request(`/fast/lookup?loan_ref=${encodeURIComponent(loanRef)}`);
    },

    /** List all simulation records (dev only — 403 when FAST_API_URL is set). */
    simCatalogue(): Promise<{
      source: string;
      count: number;
      records: Array<{
        loan_ref: string;
        borrower_name: string;
        asset_category: string;
        asset_description: string;
        amount_etb: number;
        location_zone: string;
        branch: string;
      }>;
    }> {
      return request("/fast/sim/catalogue");
    },

    /** Fetch one full simulation record by loan ref (dev only). */
    simRecord(loanRef: string): Promise<{
      data: {
        loan_detail: Record<string, unknown>;
        borrower_info: Record<string, unknown>;
        asset_info: Record<string, unknown>;
        fetched_at: string;
        source: string;
      };
    }> {
      return request(`/fast/sim/record/${encodeURIComponent(loanRef)}`);
    },
  },

  auth: {
    login(email: string, password: string): Promise<{ data: { access_token: string; refresh_token: string; user: { name: string; role: string } } }> {
      return request("/auth/login", {
        method: "POST",
        body: JSON.stringify({ email, password }),
      });
    },
    me(): Promise<{ data: { id: string; name: string; role: string; email: string; institution_id?: string; phone?: string; is_active: boolean; created_at: string } }> {
      return request("/auth/me");
    },
    updateMe(fields: { name?: string; phone?: string }): Promise<{ data: { id: string; name: string; role: string; email: string } }> {
      return request("/auth/me", {
        method: "PATCH",
        body: JSON.stringify(fields),
      });
    },
    changePassword(currentPassword: string, newPassword: string): Promise<{ data: { access_token: string; refresh_token: string } }> {
      return request("/auth/change-password", {
        method: "POST",
        body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
      });
    },
  },

  reports: {
    financial(params: { from?: string; to?: string; lender_id?: string }): Promise<{
      data: {
        rows: Array<Record<string, unknown>>;
        summary: {
          total_cases: number;
          total_loan_value: number;
          total_settled: number;
          average_recovery_pct: number;
          highest_single_recovery: number;
        };
        generated_at: string;
      };
    }> {
      const q = new URLSearchParams();
      if (params.from) q.set("from", params.from);
      if (params.to) q.set("to", params.to);
      if (params.lender_id) q.set("lender_id", params.lender_id);
      const qs = q.toString();
      return request(`/cases/reports/financial${qs ? `?${qs}` : ""}`);
    },

    execution(params: { from?: string; to?: string; lender_id?: string }): Promise<{
      data: {
        step_durations: Array<Record<string, unknown>>;
        stalled_cases: Array<Record<string, unknown>>;
        monthly_throughput: Array<Record<string, unknown>>;
        generated_at: string;
      };
    }> {
      const q = new URLSearchParams();
      if (params.from) q.set("from", params.from);
      if (params.to) q.set("to", params.to);
      if (params.lender_id) q.set("lender_id", params.lender_id);
      const qs = q.toString();
      return request(`/cases/reports/execution${qs ? `?${qs}` : ""}`);
    },

    statistical(params: { from?: string; to?: string; lender_id?: string }): Promise<{
      data: {
        by_asset_category: Array<Record<string, unknown>>;
        by_geographic_zone: Array<Record<string, unknown>>;
        portfolio_summary: { total: number; active: number; recovered: number; recovery_rate: number };
        generated_at: string;
      };
    }> {
      const q = new URLSearchParams();
      if (params.from) q.set("from", params.from);
      if (params.to) q.set("to", params.to);
      if (params.lender_id) q.set("lender_id", params.lender_id);
      const qs = q.toString();
      return request(`/cases/reports/statistical${qs ? `?${qs}` : ""}`);
    },
  },

  complaints: {
    list(params?: { case_id?: string; status?: string; page?: number; limit?: number }): Promise<{
      data: Array<Record<string, unknown>>;
      total: number;
      page: number;
      limit: number;
    }> {
      const q = new URLSearchParams();
      if (params?.case_id) q.set("case_id", params.case_id);
      if (params?.status) q.set("status", params.status);
      if (params?.page) q.set("page", String(params.page));
      if (params?.limit) q.set("limit", String(params.limit));
      const qs = q.toString();
      return request(`/complaints${qs ? `?${qs}` : ""}`);
    },

    get(id: string): Promise<{ data: Record<string, unknown> }> {
      return request(`/complaints/${id}`);
    },

    create(body: { case_id: string; raised_by: string; category: string; description: string }): Promise<{ data: Record<string, unknown> }> {
      return request("/complaints", {
        method: "POST",
        body: JSON.stringify(body),
      });
    },

    addNote(id: string, text: string, authorId: string): Promise<{ data: Record<string, unknown> }> {
      return request(`/complaints/${id}/notes`, {
        method: "POST",
        body: JSON.stringify({ text, author_id: authorId }),
      });
    },

    updateStatus(id: string, status: "open" | "in_review" | "resolved", actorId: string): Promise<{ data: Record<string, unknown> }> {
      return request(`/complaints/${id}/status`, {
        method: "PATCH",
        body: JSON.stringify({ status, actor_id: actorId }),
      });
    },
  },
} as const;
